import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Send, MoreVertical, Loader2, ArrowLeft } from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { SimpleChatButton, SimpleChatStage } from '@/lib/api';

interface Message {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
}

interface SimpleChatWindowProps {
    messages: Message[];
    buttons: SimpleChatButton[];
    expectingText: boolean;
    inputPlaceholder: string;
    onButtonClick: (action: string) => void;
    onTextSubmit: (text: string) => void;
    onClose: () => void;
    onEndChat: () => void;
    isLoading: boolean;
    stage: SimpleChatStage;
}

// Parse markdown links in text
const parseMarkdownLinks = (text: string) => {
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts: (string | JSX.Element)[] = [];
    let lastIndex = 0;
    let match;
    let keyIndex = 0;

    while ((match = linkRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
        }

        parts.push(
            <a
                key={keyIndex++}
                href={match[2]}
                target="_blank"
                rel="noopener noreferrer"
                className="text-secondary hover:text-secondary/80 underline underline-offset-2 transition-colors"
            >
                {match[1]}
            </a>
        );

        lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }

    return parts.length > 0 ? parts : text;
};

// Get stage title
const getStageTitle = (stage: SimpleChatStage): string => {
    switch (stage) {
        case 'MAIN_MENU': return 'القائمة الرئيسية';
        case 'SELECT_PROJECT_TYPE': return 'اختيار نوع المشروع';
        case 'ADD_DETAILS_CONFIRM': return 'تأكيد التفاصيل';
        case 'SELECT_FEATURES': return 'اختيار الميزات';
        case 'ENTER_PHONE': return 'إدخال الهاتف';
        case 'SELECT_BUDGET': return 'اختيار الميزانية';
        case 'COMPLETED': return 'اكتمل الطلب';
        case 'SERVICES_INFO': return 'الأسعار والخدمات';
        case 'PORTFOLIO_INFO': return 'أعمالنا السابقة';
        case 'CONTACT_MANAGER': return 'تواصل مع المدير';
        default: return 'مساعد TRBD';
    }
};

const SimpleChatWindow = ({
    messages,
    buttons,
    expectingText,
    inputPlaceholder,
    onButtonClick,
    onTextSubmit,
    onClose,
    onEndChat,
    isLoading,
    stage,
}: SimpleChatWindowProps) => {
    const [input, setInput] = useState('');
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Auto-scroll to bottom on new messages
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, buttons]);

    // Focus input when expecting text
    useEffect(() => {
        if (expectingText) {
            inputRef.current?.focus();
        }
    }, [expectingText]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (input.trim() && !isLoading) {
            onTextSubmit(input.trim());
            setInput('');
        }
    };

    // Separate regular buttons from back/navigation buttons
    const regularButtons = buttons.filter(btn => !btn.action.startsWith('back_'));
    const backButton = buttons.find(btn => btn.action.startsWith('back_'));

    // Check if it's feature selection mode
    const isFeatureSelection = stage === 'SELECT_FEATURES';
    const featureButtons = isFeatureSelection
        ? regularButtons.filter(btn => btn.action.startsWith('feature_'))
        : [];
    const actionButtons = isFeatureSelection
        ? regularButtons.filter(btn => !btn.action.startsWith('feature_'))
        : regularButtons;

    return (
        <div className="bg-card/95 backdrop-blur-xl rounded-3xl border border-white/10 shadow-2xl shadow-black/50 overflow-hidden flex flex-col h-[700px]">
            {/* Header */}
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-primary/10 to-secondary/10">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                        <span className="text-white font-bold text-sm">T</span>
                    </div>
                    <div>
                        <h3 className="font-bold text-foreground text-sm">TRBD</h3>
                        <span className="text-xs text-muted-foreground">{getStageTitle(stage)}</span>
                    </div>
                </div>

                <div className="flex items-center gap-1">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button className="p-2 rounded-lg hover:bg-white/5 transition-colors text-muted-foreground hover:text-foreground">
                                <MoreVertical className="w-4 h-4" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-card border-border">
                            <DropdownMenuItem onClick={onEndChat} className="text-destructive focus:text-destructive cursor-pointer">
                                إنهاء المحادثة
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg hover:bg-white/5 transition-colors text-muted-foreground hover:text-foreground"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.map((message) => (
                    <motion.div
                        key={message.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex ${message.role === 'user' ? 'justify-start' : 'justify-end'}`}
                    >
                        <div
                            className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${message.role === 'user'
                                ? 'bg-primary/20 text-foreground rounded-tr-md'
                                : 'bg-white/5 text-foreground rounded-tl-md border border-white/5'
                                }`}
                        >
                            {parseMarkdownLinks(message.content)}
                        </div>
                    </motion.div>
                ))}

                {/* Loading indicator */}
                {isLoading && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex justify-end"
                    >
                        <div className="bg-white/5 px-4 py-3 rounded-2xl rounded-tl-md border border-white/5">
                            <div className="flex items-center gap-2 text-muted-foreground">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span className="text-sm">جاري المعالجة...</span>
                            </div>
                        </div>
                    </motion.div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {/* Buttons Section */}
            {!isLoading && buttons.length > 0 && (
                <div className="px-3 pb-2 max-h-[400px] overflow-y-auto custom-scrollbar space-y-2">
                    {/* Feature Toggle Buttons (Grid) */}
                    {isFeatureSelection && featureButtons.length > 0 && (
                        <div className="grid grid-cols-2 gap-2">
                            {featureButtons.map((btn) => (
                                <motion.button
                                    key={btn.action}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() => onButtonClick(btn.action)}
                                    className={`px-3 py-2.5 rounded-xl text-sm font-medium transition-all text-right ${btn.selected
                                        ? 'bg-primary/30 border-2 border-primary text-primary-foreground shadow-lg shadow-primary/20'
                                        : 'bg-white/5 border border-white/10 text-foreground hover:bg-white/10 hover:border-white/20'
                                        }`}
                                >
                                    {btn.text}
                                </motion.button>
                            ))}
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className={`grid gap-2 ${actionButtons.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                        {actionButtons.map((btn) => (
                            <motion.button
                                key={btn.action}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => onButtonClick(btn.action)}
                                className={`px-4 py-3 rounded-xl text-sm font-medium border transition-all flex items-center justify-center text-center h-full min-h-[44px]
                                    ${actionButtons.length === 1
                                        ? 'bg-gradient-to-r from-primary to-secondary text-white border-transparent shadow-lg shadow-primary/20'
                                        : 'bg-white/5 border-white/10 text-foreground hover:bg-white/10 hover:border-white/20 hover:shadow-md'
                                    }`}
                            >
                                {btn.text}
                            </motion.button>
                        ))}
                    </div>

                    {/* Back Button */}
                    {backButton && (
                        <motion.button
                            whileHover={{ scale: 1.01 }}
                            whileTap={{ scale: 0.99 }}
                            onClick={() => onButtonClick(backButton.action)}
                            className="w-full px-4 py-2.5 rounded-xl text-sm font-medium bg-white/5 border border-white/10 text-muted-foreground hover:bg-white/10 hover:text-foreground transition-all flex items-center justify-center gap-2 sticky bottom-0 backdrop-blur-md"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            {backButton.text}
                        </motion.button>
                    )}
                </div>
            )}

            {/* Text Input (when expecting text) */}
            {expectingText && (
                <form onSubmit={handleSubmit} className="p-3 border-t border-white/10">
                    <div className="flex items-center gap-2">
                        <input
                            ref={inputRef}
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder={inputPlaceholder || 'اكتب هنا...'}
                            disabled={isLoading}
                            className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all disabled:opacity-50 text-right"
                            dir="auto"
                            maxLength={500}
                        />
                        <button
                            type="submit"
                            disabled={!input.trim() || isLoading}
                            className="p-3 rounded-xl bg-gradient-to-br from-primary to-secondary text-white disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-primary/30 transition-all"
                        >
                            <Send className="w-4 h-4" />
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
};

export default SimpleChatWindow;
